import React, { useCallback, useEffect, useState } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

type ImageNatural = { w: number; h: number }

export const useFitToViewport = (
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  setMinScale: (scale: number) => void,
  imageDimensions: ImageNatural,
  isMultipage: boolean,
  allImagesLoaded: boolean,
  paddingPx: number = 0,
  autoFitEnabled: boolean = true
) => {
  const [fitComplete, setFitComplete] = useState(true)

  const calculateFitScale = useCallback(() => {
    if (!imageDimensions || !transformRef.current || !allImagesLoaded)
      return null

    const wrapper = transformRef.current.instance.wrapperComponent
    const contentComponent = transformRef.current.instance.contentComponent
    const currentState = transformRef.current.instance.transformState

    if (!wrapper || !contentComponent) return null

    const wrapRect = wrapper.getBoundingClientRect()
    const contentRect = contentComponent.getBoundingClientRect()

    if (wrapRect.width === 0 || wrapRect.height === 0) return null

    const currentScale = currentState.scale
    const contentWidthAtScale1 = contentRect.width / currentScale

    if (isMultipage) {
      const actualContentWidth = contentWidthAtScale1
      const actualContentHeight = contentRect.height / currentScale

      const scaleX = (wrapRect.width - paddingPx * 2) / actualContentWidth
      const scaleY = (wrapRect.height - paddingPx * 2) / actualContentHeight
      const targetScale = Math.min(scaleX, scaleY)

      return {
        targetScale,
        initialScaleX: 1,
        wrapRect,
        actualContentWidth,
        actualContentHeight,
      }
    } else {
      const initialScaleX = contentWidthAtScale1 / imageDimensions.w

      const safeInitialScaleX =
        initialScaleX > 0 && initialScaleX < 1000 && !isNaN(initialScaleX)
          ? initialScaleX
          : 1

      const scaleX = (wrapRect.width - paddingPx * 2) / imageDimensions.w
      const scaleY = (wrapRect.height - paddingPx * 2) / imageDimensions.h
      const targetScaleInImageSpace = Math.min(scaleX, scaleY)

      return {
        targetScale: targetScaleInImageSpace / safeInitialScaleX,
        initialScaleX: safeInitialScaleX,
        wrapRect,
      }
    }
  }, [imageDimensions, transformRef, isMultipage, allImagesLoaded, paddingPx])

  const fitImageToViewport = useCallback(
    (animationTime = 0) => {
      if (!transformRef.current) return

      const result = calculateFitScale()
      if (!result) return

      const { targetScale, initialScaleX, wrapRect } = result

      // Update minimum scale to prevent zooming out beyond fit
      setMinScale(targetScale)

      const isMultipageCalculation =
        isMultipage &&
        'actualContentWidth' in result &&
        result.actualContentWidth !== undefined &&
        result.actualContentHeight !== undefined &&
        imageDimensions

      const imgCenterX = imageDimensions.w / 2
      const imgCenterY = imageDimensions.h / 2

      const centerInDisplayX = isMultipageCalculation
        ? imgCenterX
        : imgCenterX * initialScaleX
      const centerInDisplayY = isMultipageCalculation
        ? imgCenterY
        : imgCenterY * initialScaleX

      const positionX = wrapRect.width / 2 - centerInDisplayX * targetScale
      const positionY = wrapRect.height / 2 - centerInDisplayY * targetScale

      transformRef.current.setTransform(
        positionX,
        positionY,
        targetScale,
        animationTime
      )
    },
    [imageDimensions, calculateFitScale, transformRef, setMinScale, isMultipage]
  )

  const resetToFit = useCallback(() => {
    fitImageToViewport(200)
  }, [fitImageToViewport])

  // Recalculate minScale on window resize
  useEffect(() => {
    if (!imageDimensions) return

    const updateMinScale = () => {
      const result = calculateFitScale()
      if (result) {
        setMinScale(result.targetScale)
      }
    }
    updateMinScale()

    window.addEventListener('resize', updateMinScale)
    return () => window.removeEventListener('resize', updateMinScale)
  }, [imageDimensions, calculateFitScale, setMinScale])

  // Set fit complete flag after fit is applied
  useEffect(() => {
    if (imageDimensions && transformRef.current && allImagesLoaded) {
      if (!autoFitEnabled) {
        setFitComplete(true)
        return
      }

      fitImageToViewport(0)
      // Small delay to ensure transform is applied
      const timer = setTimeout(() => {
        setFitComplete(true)
      }, 100)
      return () => clearTimeout(timer)
    }

    setFitComplete(false)
  }, [
    allImagesLoaded,
    autoFitEnabled,
    fitImageToViewport,
    imageDimensions,
    transformRef,
  ])

  return {
    imgNatural: imageDimensions,
    resetToFit,
    onFitComplete: fitComplete,
  }
}
